import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import {
  Search,
  Download,
  Users,
  Phone,
  Mail,
  Plus,
  CheckCircle2,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { eventRegistrationService } from "@project/services/admin/event-registration.service";

export interface AdminEventRegistrationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: any;
}

export function AdminEventRegistrationsDialog({
  open,
  onOpenChange,
  event,
}: AdminEventRegistrationsDialogProps) {
  const [search, setSearch] = useState("");
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // New offline registrant state
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newGuests, setNewGuests] = useState("1");
  const [newNeeds, setNewNeeds] = useState("");

  const fetchRegistrations = useCallback(async () => {
    if (!event?.id) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await eventRegistrationService.listPage({
        event: event.id,
        page_size: 200,
      });
      setRegistrations(result.data || []);
    } catch (err) {
      console.error("Failed to load registrations:", err);
      projectToast.error("Failed to load registrations");
    } finally {
      setIsLoading(false);
    }
  }, [event?.id]);

  useEffect(() => {
    if (open) {
      fetchRegistrations();
    }
  }, [open, fetchRegistrations]);

  if (!event) {
    return null;
  }

  const filteredRegistrations = registrations.filter(r => {
    const nameMatch = (r.full_name ?? "")
      .toLowerCase()
      .includes(search.toLowerCase());
    const phoneVal = r.phone ?? r.mobile_number ?? "";
    const phoneMatch = phoneVal.includes(search);
    const refMatch = (r.booking_ref ?? "")
      .toLowerCase()
      .includes(search.toLowerCase());
    return nameMatch ?? phoneMatch ?? refMatch;
  });

  const totalGuests = registrations.reduce(
    (sum, r) => sum + Number(r.guests ?? r.total_attendees ?? 0),
    0
  );

  const exportCSV = () => {
    const headers = [
      "Booking Ref,Full Name,Phone,Email,Guests,Special Needs,Status,Registration Date\n",
    ];
    const rows = registrations.map(
      r =>
        `"${r.booking_ref}","${r.full_name}","${r.phone ?? r.mobile_number ?? ""}","${r.email ?? ""}","${r.guests ?? r.total_attendees ?? 0}","${r.special_needs ?? r.notes ?? ""}","${r.status}","${r.created_at ?? ""}"`
    );
    const blob = new Blob([...headers, rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registrations-${event.id ?? "event"}.csv`;
    a.click();
  };

  const toggleStatus = async (id: string | number, currentStatus: string) => {
    try {
      const nextStatus =
        currentStatus === "Confirmed" ? "Attended" : "Confirmed";
      await eventRegistrationService.update(id, {
        status: nextStatus,
        event: event.id,
        full_name: registrations.find(r => r.id === id)?.full_name,
        mobile_number:
          registrations.find(r => r.id === id)?.mobile_number ??
          registrations.find(r => r.id === id)?.phone,
        total_attendees: Number(
          registrations.find(r => r.id === id)?.total_attendees ??
            registrations.find(r => r.id === id)?.guests ??
            1
        ),
      });
      projectToast.success(`Status updated to ${nextStatus}`);
      fetchRegistrations();
    } catch (err) {
      console.error("Failed to update status:", err);
      projectToast.error("Failed to update status");
    }
  };

  const handleAddManualRegistrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newPhone.trim()) {
      projectToast.error("Please enter Name and Phone number");
      return;
    }

    try {
      const result = await eventRegistrationService.create({
        event: event.id,
        full_name: newName.trim(),
        mobile_number: newPhone.trim(),
        email: newEmail.trim() || undefined,
        total_attendees: Number(newGuests) || 1,
        notes: newNeeds.trim() || "Offline Entry",
        status: "Confirmed",
      });

      projectToast.success(`Registrant added! Pass: ${result.booking_ref}`);
      setNewName("");
      setNewPhone("");
      setNewEmail("");
      setNewGuests("1");
      setNewNeeds("");
      setShowAddForm(false);
      fetchRegistrations();
    } catch (err) {
      console.error("Failed to add registrant:", err);
      projectToast.error("Failed to add registrant");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto p-6 sm:rounded-2xl">
        <DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold text-foreground">
              <Users className="h-5 w-5 text-primary" />
              Event Registrations —{" "}
              <span className="text-primary">{event.title}</span>
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={showAddForm ? "secondary" : "default"}
                onClick={() => setShowAddForm(!showAddForm)}
                className="gap-1 text-xs"
              >
                <Plus className="h-3.5 w-3.5" />
                {showAddForm ? "Close Form" : "Add Offline Entry"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={exportCSV}
                className="gap-1 text-xs"
              >
                <Download className="h-3.5 w-3.5" />
                Export CSV
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Offline Registrant Addition Form */}
        {showAddForm && (
          <form
            onSubmit={handleAddManualRegistrant}
            className="border-primary/30 bg-primary/5 mt-4 space-y-3 rounded-xl border p-4"
          >
            <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
              Add Manual / Phone Registrant
            </h4>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-[11px] font-semibold">
                  Attendee Full Name *
                </Label>
                <Input
                  required
                  placeholder="e.g. Ramesh Shah"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="mt-1 h-8 bg-background text-xs"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold">
                  Mobile Number *
                </Label>
                <Input
                  required
                  placeholder="+91 98250 12345"
                  value={newPhone}
                  onChange={e => setNewPhone(e.target.value)}
                  className="mt-1 h-8 bg-background text-xs"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold">
                  Email Address
                </Label>
                <Input
                  placeholder="name@gmail.com"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  className="mt-1 h-8 bg-background text-xs"
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-[11px] font-semibold">
                  Guests Count
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={newGuests}
                  onChange={e => setNewGuests(e.target.value)}
                  className="mt-1 h-8 bg-background text-xs"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] font-semibold">
                  Special Assistance / Notes
                </Label>
                <Input
                  placeholder="Wheelchair, front row, etc."
                  value={newNeeds}
                  onChange={e => setNewNeeds(e.target.value)}
                  className="mt-1 h-8 bg-background text-xs"
                />
              </div>
            </div>
            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                size="sm"
                className="bg-primary text-xs font-semibold text-primary-foreground"
              >
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Confirm
                Registration
              </Button>
            </div>
          </form>
        )}

        {/* Summary Badges & Search */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          <div className="flex items-center gap-3">
            <span className="rounded-lg bg-secondary px-3 py-1 text-xs font-semibold">
              Total Registrations:{" "}
              <strong className="text-primary">{registrations.length}</strong>
            </span>
            <span className="rounded-lg bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700">
              Expected Attendees: <strong>{totalGuests} Persons</strong>
            </span>
          </div>

          <div className="relative w-64">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search registrant or ref..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 text-xs"
            />
          </div>
        </div>

        {/* Registrations Table */}
        <div className="mt-4 overflow-x-auto rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/40">
                <TableHead className="text-xs">Booking Ref</TableHead>
                <TableHead className="text-xs">Registrant Name</TableHead>
                <TableHead className="text-xs">Contact Details</TableHead>
                <TableHead className="text-center text-xs">Guests</TableHead>
                <TableHead className="text-xs">Special Assistance</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-right text-xs">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRegistrations.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-8 text-center text-xs text-muted-foreground"
                  >
                    No registrations found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRegistrations.map(r => (
                  <TableRow key={r.id} className="text-xs">
                    <TableCell className="font-mono font-bold text-primary">
                      {r.booking_ref}
                    </TableCell>
                    <TableCell className="font-medium text-foreground">
                      {r.full_name}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5 text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-gold" />{" "}
                          {r.phone ?? r.mobile_number}
                        </div>
                        {r.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-gold" /> {r.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-bold text-foreground">
                      {r.guests ?? r.total_attendees}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-[11px] text-muted-foreground">
                      {r.special_needs ?? r.notes ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={
                          r.status === "Attended"
                            ? "bg-emerald-500/15 font-bold text-emerald-700"
                            : "bg-blue-500/15 font-bold text-blue-700"
                        }
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStatus(r.id, r.status)}
                        className="h-7 text-[11px]"
                      >
                        {r.status === "Confirmed"
                          ? "Mark Attended"
                          : "Mark Confirmed"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
